import axios, { type InternalAxiosRequestConfig } from "axios";

import { env } from "../config/env";
import { normalizeApiError } from "../errors/normalizeApiError";
import {
  getAccessToken,
  notifyUnauthorized,
  recoverAccessToken,
  setAccessToken,
} from "../auth/authSession";

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

export const httpClient = axios.create({
  baseURL: env.apiUrl,
  timeout: env.apiTimeoutMs,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

httpClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (
      !axios.isAxiosError(error) ||
      error.response?.status !== 401 ||
      !error.config
    ) {
      return Promise.reject(normalizeApiError(error));
    }

    const request = error.config as RetryableRequestConfig;
    if (request._authRetry) {
      notifyUnauthorized();
      return Promise.reject(normalizeApiError(error));
    }

    request._authRetry = true;
    try {
      const token = await recoverAccessToken();
      if (!token) {
        notifyUnauthorized();
        return Promise.reject(normalizeApiError(error));
      }
      setAccessToken(token);
      request.headers.set("Authorization", `Bearer ${token}`);
      return httpClient.request(request);
    } catch (refreshError) {
      notifyUnauthorized();
      return Promise.reject(normalizeApiError(refreshError));
    }
  },
);
